import { useEffect, useMemo, useState } from "react";

import {
  ORDER_TYPE_ENUM,
  STORE_STATUS_ENUM,
  platformKey,
  titleCase,
  type PlatformKey,
} from "@/constants/enums";
import {
  BREAK_MESSAGE,
  CLOSED_STORE_MESSAGE,
  OPEN_STORE_MESSAGE,
  ORDERS_ON_HOLD_MESSAGE,
  STAFF_MESSAGE,
} from "@/constants/messages";
import { useAppSelector } from "@/store";
import { selectFormattedShifts } from "@/store/slices/shiftsSlice";
import type { StoreStatusMap } from "@/types";
import { evaluateShifts, isOrderingStopped } from "@/utils/time";

/**
 * Evaluates the trading hours for every enabled order type and returns
 * `{ [platformKey]: { status, message } }`.
 */
export default function useStoreStatus(): StoreStatusMap {
  const { formattedShifts } = useAppSelector(selectFormattedShifts);
  const {
    closed_store_message: closedStoreMessage,
    break_time_message: breakTimeMessage,
    web_stop_taking_orders: stopTakingOrders,
    web_enable_delivery: deliveryEnabled,
    web_enable_takeaway: takeawayEnabled,
    web_enable_dine_in: dineInEnabled,
    operational_time_zone_offset_gmt: utcOffsetHours,
    web_allow_ordering_till_closing_time: allowOrderingTillClosingTime,
    default_prep_minutes: prepMinutes,
  } = useAppSelector((state) => state.settings.data);
  const isStaff = useAppSelector((state) => state.auth.isStaff);
  const runStoreStatusCheck = useAppSelector(
    (state) => state.common.runStoreStatusCheck,
  );

  const [status, setStatus] = useState<StoreStatusMap>({});

  const platforms = useMemo<Array<[PlatformKey, boolean | undefined]>>(
    () => [
      [platformKey(ORDER_TYPE_ENUM.DELIVERY), deliveryEnabled],
      [platformKey(ORDER_TYPE_ENUM.TAKE_AWAY), takeawayEnabled],
      [platformKey(ORDER_TYPE_ENUM.DINE_IN), dineInEnabled],
    ],
    [deliveryEnabled, takeawayEnabled, dineInEnabled],
  );

  useEffect(() => {
    if (!runStoreStatusCheck) return;

    const next: StoreStatusMap = {};
    const closedMessage = closedStoreMessage || CLOSED_STORE_MESSAGE;

    platforms.forEach(([key, enabled]) => {
      if (isStaff) {
        next[key] = { status: STORE_STATUS_ENUM.OPEN, message: STAFF_MESSAGE };
        return;
      }

      const orderTypeLabel = titleCase(key.split("_")[1] ?? "");
      const daysForPlatform = formattedShifts[key];

      if (!enabled || !daysForPlatform) {
        next[key] = {
          status: STORE_STATUS_ENUM.CLOSED,
          message: enabled
            ? `⚠️ No timings available for ${orderTypeLabel}.`
            : `⚠️ ${orderTypeLabel} is not enabled.`,
        };
        return;
      }

      // The window arithmetic lives in `evaluateShifts` so it can be unit
      // tested against midnight-crossing hours, split sittings and empty days.
      const outcome = evaluateShifts(
        daysForPlatform,
        new Date(),
        utcOffsetHours,
        { allowOrderingTillClosingTime, prepMinutes },
      );

      if (outcome === STORE_STATUS_ENUM.OPEN) {
        next[key] = isOrderingStopped(stopTakingOrders, key, isStaff)
          ? {
              status: STORE_STATUS_ENUM.BREAK,
              message: ORDERS_ON_HOLD_MESSAGE,
            }
          : { status: STORE_STATUS_ENUM.OPEN, message: OPEN_STORE_MESSAGE };
        return;
      }

      next[key] =
        outcome === STORE_STATUS_ENUM.BREAK
          ? {
              status: STORE_STATUS_ENUM.BREAK,
              message: breakTimeMessage || BREAK_MESSAGE,
            }
          : { status: STORE_STATUS_ENUM.CLOSED, message: closedMessage };
    });

    setStatus(next);
  }, [
    platforms,
    formattedShifts,
    closedStoreMessage,
    stopTakingOrders,
    runStoreStatusCheck,
    isStaff,
    utcOffsetHours,
    breakTimeMessage,
    allowOrderingTillClosingTime,
    prepMinutes,
  ]);

  return status;
}
